// Collection bar sheets — the Filter and Sort boxes share one behaviour:
// a bottom sheet on mobile, an anchored dropdown box on desktop.
//
// Two things are worth knowing about the implementation:
//
// 1. Portalling. The panels render inside .collection-sortbar, which is
//    `position: sticky; z-index: 10` and therefore a stacking context —
//    so a panel's own z-index is trapped beneath it and the fixed site
//    header (z-index 20) paints over a full-bleed sheet. On mobile the
//    panel is moved to <body> so it escapes that context. On desktop it
//    stays put, because `position: absolute` has to resolve against the
//    trigger, and a dropdown hanging below the bar never reaches the
//    header anyway.
//
// 2. In-place updates. Choosing a value must not reload the page: a
//    reload closed the sheet and lost your place, so drilling meant
//    starting over. Links are intercepted and the section is re-fetched
//    through Shopify's Section Rendering API (the same `section_id`
//    mechanism collection-load-more.js uses), then applied piecemeal —
//    the sheet element itself is never replaced, which is what used to
//    flash the product grid mid-drill. Links stay real links, so this
//    degrades to plain navigation if the fetch fails or JS never runs.
const MOBILE_QUERY = '(max-width: 767px)';
// Kept with collection-filter-kind, which is the other place that names them.
const TAXONOMY_PARAMS = [
	'filter.p.m.custom.top_category',
	'filter.p.m.custom.tier_1',
	'filter.p.product_type',
];
const RESUME_KEY = 'sylvieFilterDrawerResume';

class SheetMenu extends HTMLElement {
	connectedCallback() {
		this.panel = this.querySelector('[data-sheet-panel]');
		this.trigger = this.querySelector('[data-action="toggle"]');
		if (!this.panel) return;
		// Captured before any portalling so the panel can be put back.
		this.homeParent = this.panel.parentElement;
		// Matches the collection grid and the search results section alike.
		this.section = this.closest('[data-section-id]');

		// Bound separately: once portalled the panel no longer bubbles into
		// this element, and while it is still inside, host-level handling has
		// to skip panel events or every click would be handled twice.
		this.onHostClick = (event) => {
			if (this.panel.contains(event.target)) return;
			this.handle(event);
		};
		this.onPanelClick = (event) => this.handle(event);
		this.onKeydown = (event) => {
			if (event.key === 'Escape' && !this.panel.hidden) this.close();
		};
		this.onDocumentClick = (event) => {
			if (this.panel.hidden) return;
			if (this.panel.contains(event.target) || this.contains(event.target)) return;
			this.close();
		};

		this.addEventListener('click', this.onHostClick);
		this.panel.addEventListener('click', this.onPanelClick);
		document.addEventListener('keydown', this.onKeydown);
		document.addEventListener('click', this.onDocumentClick);

		this.init?.();
	}

	disconnectedCallback() {
		document.removeEventListener('keydown', this.onKeydown);
		document.removeEventListener('click', this.onDocumentClick);
		// A portalled panel lives outside this element, so removing the
		// section wouldn't take it with it.
		if (this.panel && this.panel.parentElement === document.body) this.panel.remove();
		// Removed while open: nothing else will clear these, and a stale
		// sheetOpen keeps the chip row hidden.
		if (!document.querySelector('[data-sheet-panel]:not([hidden])')) {
			delete document.body.dataset.sheetOpen;
		}
		document.body.style.overflow = '';
	}

	isSamePath(href) {
		try {
			return new URL(href, window.location.origin).pathname === window.location.pathname;
		} catch (e) {
			return false;
		}
	}

	isMobile() {
		return window.matchMedia(MOBILE_QUERY).matches;
	}

	// Re-evaluated on every open so a resize between opens lands correctly.
	place() {
		const target = this.isMobile() ? document.body : this.homeParent;
		if (this.panel.parentElement !== target) target.appendChild(this.panel);
	}

	open() {
		this.place();
		this.panel.hidden = false;
		this.trigger?.setAttribute('aria-expanded', 'true');
		// Suppresses the chip row while open (see collection-results-bar).
		document.body.dataset.sheetOpen = '';
		if (this.isMobile()) document.body.style.overflow = 'hidden';
		this.afterOpen?.();
	}

	close() {
		this.panel.hidden = true;
		this.trigger?.setAttribute('aria-expanded', 'false');
		if (!document.querySelector('[data-sheet-panel]:not([hidden])')) {
			delete document.body.dataset.sheetOpen;
		}
		document.body.style.overflow = '';
	}

	toggle() {
		if (this.panel.hidden) this.open();
		else this.close();
	}

	handle(event) {
		const link = event.target.closest('[data-sheet-link]');
		// Only swap the section in place when the link stays on this template.
		// Clearing a search term sends you to /collections/all, a different
		// template whose section id differs — fetching this section against
		// that path would return the wrong markup, so let it navigate.
		if (link && link.href && this.section?.dataset.sectionId && this.isSamePath(link.href)) {
			event.preventDefault();
			this.navigate(link.href);
			return;
		}
		const target = event.target.closest('[data-action]');
		if (!target) return;
		const { action } = target.dataset;
		if (action === 'toggle') this.toggle();
		else if (action === 'close') this.close();
		else this.onAction?.(action, target);
	}

	async navigate(href) {
		if (this.loading) return;
		this.loading = true;
		this.panel.setAttribute('data-loading', '');

		const url = new URL(href, window.location.origin);
		url.searchParams.set('section_id', this.section.dataset.sectionId);

		try {
			const res = await fetch(url.toString());
			if (!res.ok) throw new Error('Section fetch failed: ' + res.status);
			const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

			const clean = new URL(href, window.location.origin);
			clean.searchParams.delete('section_id');
			history.pushState({}, '', clean.pathname + clean.search);

			this.applySection(doc);
			this.loading = false;
			this.panel.removeAttribute('data-loading');
			// After the flag clears, never inside applySection. This asks for
			// another navigate when the phase it landed on is empty, and
			// navigate refuses to start while one is already running — so from
			// in there it did nothing at all, silently. Filtering to a shelf
			// that is entirely sold out through the sheet sat on the empty
			// state, while the same URL typed in worked, because that path
			// never goes through here.
			fallBackToSoldOut();
			this.afterNavigate?.();
		} catch (e) {
			// Fall back to a normal page load.
			this.loading = false;
			this.panel.removeAttribute('data-loading');
			try {
				sessionStorage.setItem(RESUME_KEY, '1');
			} catch (err) {
				/* no resume; the sheet just opens closed next load */
			}
			window.location.href = href;
		}
	}

	// Swap in the freshly rendered pieces without replacing either sheet
	// element, so open panels and their listeners survive.
	applySection(doc) {
		const swapInner = (selector) => {
			const next = doc.querySelector(selector);
			const current = document.querySelector(selector);
			if (next && current) current.innerHTML = next.innerHTML;
		};

		// Each sheet (Shop by Category, Filter & Sort) refreshes its own
		// contents and trigger. Keyed rather than hard-coded so adding a
		// third control needs no change here.
		doc.querySelectorAll('[data-sheet-key]').forEach((nextSheet) => {
			const key = nextSheet.dataset.sheetKey;
			const current = document.querySelector(`[data-sheet-key="${key}"]`);
			if (!current) return;
			current.dataset.defaultScreen = nextSheet.dataset.defaultScreen || '';
			const nextInner = nextSheet.querySelector('.filterdrawer__inner');
			// Through the panel, not the element. On a phone the panel is moved
			// to document.body so it can sit over the page, and from that point
			// it is no longer a descendant of its own tag — so looking inside
			// the tag found nothing and the sheet's markup was never replaced.
			// The sort menu went on showing the option that had been chosen
			// before last, whatever marked it; the filter rows went stale the
			// same way. Desktop puts the panel back, which is why it only ever
			// showed on a phone.
			const currentPanel = current.panel || current.querySelector('[data-sheet-panel]');
			const inner = currentPanel ? currentPanel.querySelector('.filterdrawer__inner') : null;
			if (nextInner && inner) inner.innerHTML = nextInner.innerHTML;
			// Fresh markup means freshly narrowed range domains, so the note
			// saying they have been freed has to go with the old ones.
			delete current.dataset.rangesFreed;
			const nextTrigger = nextSheet.querySelector('.cbox__trigger');
			const trigger = current.querySelector('.cbox__trigger');
			if (nextTrigger && trigger) trigger.innerHTML = nextTrigger.innerHTML;
		});

		swapInner('.search-summary'); // search only; no-op on collections
		swapInner('.collection-chips');

		// The search box is deliberately NOT swapped wholesale — that would
		// discard anything typed but not yet submitted.
		syncSearchBox();

		// Results. The grid and the empty state stand in for one another across
		// the zero-results boundary, so swapping the grid's innerHTML would drop
		// the new markup on the floor in either direction. Reconcile the region.
		const RESULTS = '.collection__grid-wrap, .collection__empty';
		const nextResults = doc.querySelector(RESULTS);
		const results = document.querySelector(RESULTS);
		if (nextResults && results) results.replaceWith(nextResults);

		const nextMore = doc.querySelector('.collection-loadmore');
		const more = document.querySelector('.collection-loadmore');
		if (more && nextMore) more.replaceWith(nextMore);
		else if (more) more.remove();
		else if (nextMore) document.querySelector('.collection__grid-wrap')?.after(nextMore);

		// Both swaps above detach nodes that collection-load-more is holding —
		// its grid and the sentinel its observer watches. The element itself
		// survives, so nothing reconnects it; it has to be told.
		document.querySelector('collection-load-more')?.resync();

		// The query lives in the URL this navigation just pushed, so the
		// in-collection match has to be redone against the new cards.
		applyCollectionSearch();
	}
}

class FilterDrawer extends SheetMenu {
	init() {
		// The chip row and the search box's clear "x" sit outside this element
		// and outside the panel, so their links would otherwise be plain
		// navigations. Bound by exactly one sheet — there are two on the page
		// now, and binding in both would fire two fetches per click.
		if (this.dataset.sheetKey === 'filters') {
			// Delegated from the containers, not the links themselves:
			// applySection replaces both the chips' innerHTML and (when a
			// search term appears) the clear link itself, so a listener
			// bound to a link would be dropped on the first update. The
			// containers outlive every swap. handle() ignores anything
			// without [data-sheet-link], so the search field and its
			// submit button are unaffected.
			this.outside = this.section?.querySelector('.collection-chips');
			this.searchForm = this.section?.querySelector('.cbox--search');
			if (this.outside) this.outside.addEventListener('click', this.onPanelClick);
			if (this.searchForm) this.searchForm.addEventListener('click', this.onPanelClick);
		}

		// Range sliders: drag to preview, release to apply.
		this.panel.addEventListener('input', (event) => {
			const input = event.target.closest('[data-range-lo], [data-range-hi]');
			if (input) this.onRangeInput(input);
		});
		this.panel.addEventListener('change', (event) => {
			const input = event.target.closest('[data-range-lo], [data-range-hi]');
			if (input) this.onRangeCommit(input);
		});
		this.syncRanges();

		try {
			if (sessionStorage.getItem(RESUME_KEY) === '1') {
				sessionStorage.removeItem(RESUME_KEY);
				this.open();
			}
		} catch (e) {
			/* privacy mode; nothing to resume */
		}
	}

	// A taxonomy screen advances to the next level after a selection —
	// that's what data-default-screen encodes. Everywhere else (sort, a
	// range, a value list) you stay put so you can keep adjusting without
	// being thrown out of the screen you're working in.
	//
	// Which screens advance is marked in the markup with data-advances
	// rather than listed here, so adding a taxonomy level doesn't mean
	// remembering to edit this file too.
	handle(event) {
		// "View results" with something held: submit it all at once.
		const view = event.target.closest('.filterdrawer__view');
		if (view && this.held) {
			event.preventDefault();
			const url = this.held;
			this.forgetHeld({ restore: false });
			this.close();
			this.navigate(url.pathname + url.search);
			return;
		}

		// A chevron opens the level below in place. The taxonomy is one list
		// now rather than three screens, so nothing is navigated and nothing
		// is fetched — the row simply grows.
		const twist = event.target.closest('[data-twist]');
		if (twist) {
			const branch = twist.closest('.filterdrawer__branch');
			// :scope, so a branch takes its own list and not the first one
			// belonging to something inside it.
			const subtree = branch?.querySelector(':scope > .filterdrawer__subtree');
			if (subtree) {
				const open = subtree.hidden;
				subtree.hidden = !open;
				twist.setAttribute('aria-expanded', open ? 'true' : 'false');
				// Shutting a branch shuts what was open inside it, so reopening
				// it shows the same thing it showed the first time.
				if (!open) {
					subtree.querySelectorAll('.filterdrawer__subtree').forEach((inner) => {
						inner.hidden = true;
					});
					subtree.querySelectorAll('[data-twist]').forEach((inner) => {
						inner.setAttribute('aria-expanded', 'false');
					});
				}
			}
			return;
		}

		// A branch chip's x drops all the types it stands for at once.
		const chipRemove = event.target.closest('[data-chip-remove]');
		if (chipRemove) {
			event.preventDefault();
			const url = new URL(window.location.href);
			const drop = new Set((chipRemove.dataset.chipRemove || '').split('|').filter(Boolean));
			const keep = url.searchParams.getAll('filter.p.product_type').filter((v) => !drop.has(v));
			url.searchParams.delete('filter.p.product_type');
			keep.forEach((v) => url.searchParams.append('filter.p.product_type', v));
			this.navigate(url.pathname + url.search);
			return;
		}

		// A checkbox takes its whole branch. Nothing is submitted until the
		// View button, so several can be set at different levels first.
		const pick = event.target.closest('[data-pick]');
		if (pick) {
			event.preventDefault();
			this.togglePick(pick);
			return;
		}

		const link = event.target.closest('[data-sheet-link]');
		if (link) {
			const screen = link.closest('.filterdrawer__screen');
			const id = screen ? screen.dataset.screenId || '' : '';
			this.stickScreen = id && !screen.hasAttribute('data-advances') ? id : null;
		}
		super.handle(event);
	}

	// Everything the taxonomy selects resolves to product types, so the held
	// state is just that set. Department and category are never applied as
	// filters: they are separate Search & Discovery filters and Shopify ANDs
	// different ones together, so Tables plus Mirrors as coarse filters is
	// nothing at all. As types they OR, and they are exactly equivalent —
	// Furniture's twelve types total 178, which is what
	// top_category=Furniture returns.
	heldTypes() {
		// Rebuilt whenever the address has moved on. This was captured once and
		// kept for the life of the element, so a chip removed from the row —
		// which navigates in place, leaving the element standing — left the
		// sheet still holding the type it had just dropped, and reopening the
		// filter showed it selected. Pending edits survive because writing
		// does not move the address.
		if (!this.held || this.heldHref !== window.location.href) {
			this.held = new URL(window.location.href);
			this.heldHref = window.location.href;
		}
		return new Set(this.held.searchParams.getAll('filter.p.product_type'));
	}

	writeHeldTypes(types) {
		TAXONOMY_PARAMS.forEach((param) => this.held.searchParams.delete(param));
		types.forEach((value) => this.held.searchParams.append('filter.p.product_type', value));
		this.paintPicks();
	}

	togglePick(button) {
		const types = (button.dataset.types || '').split('|').filter(Boolean);
		if (!types.length) return;
		const held = this.heldTypes();
		// A part-filled branch fills rather than empties: clearing what you
		// have just half-chosen is the rarer intent.
		const allOn = types.every((type) => held.has(type));
		types.forEach((type) => (allOn ? held.delete(type) : held.add(type)));
		this.writeHeldTypes(held);
	}

	// Tick, dash or empty, by how much of the branch is in the set.
	paintPicks() {
		const held = this.heldTypes();
		this.panel.querySelectorAll('[data-pick]').forEach((button) => {
			const types = (button.dataset.types || '').split('|').filter(Boolean);
			const on = types.filter((type) => held.has(type)).length;
			const state = on === 0 ? 'off' : on === types.length ? 'on' : 'some';
			button.dataset.state = state;
			button.setAttribute('aria-pressed', state === 'on' ? 'true' : state === 'some' ? 'mixed' : 'false');
		});
		this.paintViewButtons(held);
	}

	// Says what pressing it would give you. A selection that is exactly one
	// branch is named; anything else just says results. Counts are gone from
	// the filter entirely: they followed the in-stock phase, so an entirely
	// sold-out shelf read as "0" and looked like nothing to see, when the
	// whole point is that you can still go and look at it.
	paintViewButtons(held) {
		const buttons = this.panel.querySelectorAll('[data-view-button]');
		if (!buttons.length) return;

		// A range can be pending with no type chosen at all, and "View all
		// items" would be a lie about what pressing it does.
		const pending = this.held && this.held.search !== window.location.search;
		if (!held.size) {
			buttons.forEach((button) => (button.textContent = pending ? 'View results' : 'View all items'));
			return;
		}

		let named = null;
		this.panel.querySelectorAll('[data-pick][data-branch]').forEach((button) => {
			const types = (button.dataset.types || '').split('|').filter(Boolean);
			if (types.length === held.size && types.every((type) => held.has(type))) {
				named = button.dataset.branch;
			}
		});
		if (named) {
			buttons.forEach((button) => (button.textContent = 'View all ' + named));
			return;
		}

		buttons.forEach((button) => (button.textContent = 'View results'));
	}

	// Every branch starts shut, whatever is selected. Opening the ones holding
	// a selection was tried and made the control read as broken: the branch
	// you had picked from was already open, so its chevron closed it while
	// every other chevron opened — the same tap doing opposite things
	// depending on a state nobody had asked for. Which branch holds something
	// is already on the row, in the checkbox: a part-taken parent shows a dash
	// without having to be opened to prove it.
	applyNav() {}

	// Cancelling drops what was held; the checkboxes repaint from the URL.
	forgetHeld(options) {
		const restore = !options || options.restore !== false;
		this.held = null;
		this.heldRows = null;
		if (!restore) return;
		// The handles were dragged, so the DOM no longer matches the page. The
		// value attribute still does: setting .value never touches it, and
		// everything that moves a handle for good writes it back.
		this.panel.querySelectorAll('[data-range] [data-range-lo], [data-range] [data-range-hi]').forEach((input) => {
			const rendered = input.getAttribute('value');
			if (rendered !== null) input.value = rendered;
		});
		this.syncRanges();
		this.paintPicks();
	}

	afterOpen() {
		this.showScreen(this.dataset.entryScreen || 'root', 'forward');
		this.applyNav();
		this.paintPicks();
		// Once per render of this sheet. applySection replaces its innards, so
		// the flag goes with them and the next open asks again.
		if (!this.dataset.rangesFreed) {
			this.dataset.rangesFreed = '1';
			freeRangeDomains(this);
		}
	}

	// Cancel, Escape, the scrim and a click outside all land here.
	close() {
		if (this.held) this.forgetHeld();
		super.close();
	}

	afterNavigate() {
		// Adjusting a slider shouldn't eject you from that slider's screen,
		// which is where data-default-screen would otherwise send you.
		const stick = this.stickScreen;
		this.stickScreen = null;
		this.showScreen(stick || this.dataset.defaultScreen, 'forward');
		this.applyNav();
		this.paintPicks();
		this.syncRanges();
		noteRelaxedSearch();
	}

	syncRanges() {
		this.panel.querySelectorAll('[data-range]').forEach((root) => this.paintRange(root));
	}

	formatRangeValue(root, value) {
		return (root.dataset.prefix || '') + Number(value).toLocaleString() + (root.dataset.suffix || '');
	}

	paintRange(root) {
		const lo = root.querySelector('[data-range-lo]');
		const hi = root.querySelector('[data-range-hi]');
		if (!lo || !hi) return;
		const min = Number(root.dataset.min);
		const max = Number(root.dataset.max);
		const a = Number(lo.value);
		const b = Number(hi.value);
		const span = max - min || 1;
		const fill = root.querySelector('[data-range-fill]');
		if (fill) {
			fill.style.left = ((a - min) / span) * 100 + '%';
			fill.style.right = 100 - ((b - min) / span) * 100 + '%';
		}
		const out = root.querySelector('[data-range-readout]');
		if (out) {
			out.textContent = this.formatRangeValue(root, a) + ' – ' + this.formatRangeValue(root, b);
		}
	}

	// Keep the handles from crossing over each other.
	onRangeInput(input) {
		const root = input.closest('[data-range]');
		if (!root) return;
		const lo = root.querySelector('[data-range-lo]');
		const hi = root.querySelector('[data-range-hi]');
		if (Number(lo.value) > Number(hi.value)) {
			if (input === lo) lo.value = hi.value;
			else hi.value = lo.value;
		}
		this.paintRange(root);
	}

	onRangeCommit(input) {
		const root = input.closest('[data-range]');
		if (!root) return;
		const min = Number(root.dataset.min);
		const max = Number(root.dataset.max);
		const a = Number(root.querySelector('[data-range-lo]').value);
		const b = Number(root.querySelector('[data-range-hi]').value);

		// Start from anything held, so committing a slider doesn't discard
		// product types picked but not yet submitted.
		const url = this.held ? new URL(this.held) : new URL(window.location.href);
		if (this.held) this.forgetHeld({ restore: false });
		const listParam = root.dataset.paramList;
		if (listParam) {
			// Shopify exposes these as a value list, not a range, so the span
			// is expressed as every value inside it — repeated params, which
			// Shopify ORs together.
			url.searchParams.delete(listParam);
			if (a > min || b < max) {
				(root.dataset.values || '')
					.split(',')
					.map((pair) => {
						const [num, encoded] = pair.split(':');
						// Decoded here because searchParams re-encodes on append.
						return { n: Number(num), value: decodeURIComponent((encoded || '').replace(/\+/g, ' ')) };
					})
					.filter((item) => Number.isFinite(item.n) && item.n >= a && item.n <= b && item.value !== '')
					.forEach((item) => url.searchParams.append(listParam, item.value));
			}
		} else {
			// A handle parked at its bound isn't a constraint — drop the param
			// so the URL (and the chip row) don't claim a filter that isn't
			// narrowing anything.
			if (a > min) url.searchParams.set(root.dataset.paramMin, a);
			else url.searchParams.delete(root.dataset.paramMin);
			if (b < max) url.searchParams.set(root.dataset.paramMax, b);
			else url.searchParams.delete(root.dataset.paramMax);
		}

		// Held, not applied. Nothing in this sheet changes the results until
		// View results is pressed — the product types already worked that way,
		// and a slider that reloaded the grid the moment a handle was let go
		// made the other two jump under the hand that was still using them.
		this.held = url;
		this.heldHref = window.location.href;
		this.paintPicks();
	}

	onAction(action, target) {
		if (action === 'show') this.showScreen(target.dataset.showScreen, 'back');
	}

	showScreen(id, direction) {
		const screens = this.panel.querySelectorAll('.filterdrawer__screen');
		if (!screens.length) return;
		// Fall back to the first screen if the requested one isn't present,
		// so the sheet is never shown with every screen hidden.
		const exists = id && this.panel.querySelector(`.filterdrawer__screen[data-screen-id="${id}"]`);
		const targetId = exists ? id : screens[0].dataset.screenId;
		this.panel.dataset.direction = direction === 'back' ? 'back' : 'forward';
		screens.forEach((screen) => {
			screen.hidden = screen.dataset.screenId !== targetId;
		});
	}
}

// A range slider runs between the smallest and largest value still
// represented in the results. That is what keeps it honest inside a
// collection — the shop reaches 135" and a collection of chairs does not —
// but "the results" means after every other filter has had its say, and that
// makes the sliders describe the narrowing rather than offer it.
//
// Pick Decor and height came back 2-66" instead of 0-135". Drop Decor again
// and the tall things were there, but the slider had never been able to ask
// for them. The availability phase does it too, all on its own: the
// all-products collection reads 0-99" on the in-stock phase against 0-135"
// across everything, and The Collector's House 4-37" against a real 1-77".
//
// Every slider is therefore put on the range it would have with no filter
// applied at all — the collection's own range, or the search's. Shopify will
// not report that beside the narrowed one, but it is one fetch away: the same
// page asked for with the filters stripped. Once a range is chosen it still
// narrows the results like anything else; it just stops narrowing itself.
//
// Asked for when the sheet is opened rather than on load, since a page whose
// filters are never opened has no use for it.
async function freeRangeDomains(sheet) {
	// Same reason as applySection: on a phone the panel is not inside the tag.
	const panel = sheet.panel || sheet.querySelector('[data-sheet-panel]') || sheet;
	const sliders = Array.from(panel.querySelectorAll('.rangefilter[data-range]'));
	if (!sliders.length) return;

	let params;
	try {
		params = new URLSearchParams(window.location.search);
	} catch (e) {
		return;
	}

	// Which of these the shopper has actually set, before the rest is stripped.
	const isSet = (slider) =>
		[slider.dataset.paramList, slider.dataset.paramMin, slider.dataset.paramMax]
			.filter(Boolean)
			.some((name) => params.has(name));
	const chosen = new Set(sliders.filter(isSet).map((slider) => slider.dataset.range || slider.dataset.paramList));

	// Every filter, availability included — it is the theme's own paging
	// mechanism rather than anything chosen, and it hides the sold-out half of
	// the range.
	Array.from(params.keys())
		.filter((key) => key.startsWith('filter.'))
		.forEach((key) => params.delete(key));

	const sectionId = document.querySelector('collection-load-more')?.dataset.sectionId;
	if (!sectionId) return;
	params.set('section_id', sectionId);

	let doc;
	try {
		const res = await fetch(window.location.pathname + '?' + params.toString());
		if (!res.ok) return;
		doc = new DOMParser().parseFromString(await res.text(), 'text/html');
	} catch (e) {
		return; // leave the narrowed domains; they still work, just tightly
	}

	sliders.forEach((slider) => {
		const key = slider.dataset.paramList || slider.dataset.paramMin;
		if (!key) return;
		const free =
			doc.querySelector('.rangefilter[data-param-list="' + key + '"]') ||
			doc.querySelector('.rangefilter[data-param-min="' + key + '"]');
		if (!free) return;

		slider.dataset.min = free.dataset.min;
		slider.dataset.max = free.dataset.max;
		// The number-to-filter-value map has to widen with the range it maps.
		if (free.dataset.values) slider.dataset.values = free.dataset.values;

		// A slider nobody set has to sit open at its full extent. Its handles
		// were rendered against the narrowed range, so widening the range alone
		// left them parked mid-track, reading as a selection never made.
		const wasSet = chosen.has(slider.dataset.range || slider.dataset.paramList);
		slider.querySelectorAll('[data-range-lo], [data-range-hi]').forEach((input) => {
			const held = input.value;
			const isLo = input.hasAttribute('data-range-lo');
			input.min = free.dataset.min;
			input.max = free.dataset.max;
			const settled = wasSet ? held : isLo ? free.dataset.min : free.dataset.max;
			input.value = settled;
			// Written through, so cancelling a drag restores this and not the
			// value the narrowed range had been rendered with.
			input.setAttribute('value', settled);
		});

		const bounds = slider.querySelectorAll('.rangefilter__bounds span');
		const freeBounds = free.querySelectorAll('.rangefilter__bounds span');
		bounds.forEach((span, i) => {
			if (freeBounds[i]) span.textContent = freeBounds[i].textContent;
		});

		sheet.paintRange?.(slider);
	});
}

// Shopify searches descriptions as well as names, and the styling copy on
// side tables and stools says "pairs beautifully with a chair" — so a plain
// "chair" came back 442 deep with 111 side tables and 101 stools in it.
// Quoting each word helped and is kept in spirit, but it cannot separate a
// thing from a thing merely mentioned. The query is aimed at the fields that
// name a thing instead, which is the same first reading in-collection search
// gives a query, so the two now agree:
//
//   every word in the title or tags — or, for a word naming a shelf of the
//   taxonomy, anything on that shelf
//
// "chair" becomes 231 results, 231 of them Chairs. "french chair" becomes 58,
// all Chairs. Descriptions still answer, but only for a query that finds
// nothing this way — relaxSearch is that second reading, and in-collection
// search has the same one.
//
// Each word gets its own clause, never the whole string as one phrase:
// "marble table" as a phrase demands that exact wording.
//
// This lives inside q rather than in a product_type filter. A filter raises a
// chip of its own beside the query's, which is a second thing to understand
// and to undo for one thing asked once — and a collection search raises none.
//
// A query already carrying quotes or Shopify's field syntax is left alone:
// someone writing either one means it.
function buildSearchQuery(raw, scopeIndex) {
	if (raw.includes('"') || /[a-z_]+:/i.test(raw)) return raw;
	const words = raw.split(/\s+/).filter(Boolean);
	if (!words.length) return raw;

	const clause = (word) => {
		const parts = ['title:"' + word + '"', 'tag:"' + word + '"'];
		taxonomyTypesFor(word, scopeIndex).forEach((type) => {
			parts.push('product_type:"' + type + '"');
		});
		return '(' + parts.join(' OR ') + ')';
	};

	const perWord = words.map(clause).join(' AND ');

	// A shelf whose name no single word carries — "wall art", "side table".
	const whole = words.length > 1 ? taxonomyTypesFor(raw, scopeIndex) : [];
	if (!whole.length) return perWord;
	const shelf = whole.map((type) => 'product_type:"' + type + '"').join(' OR ');
	return '(' + perWord + ') OR (' + shelf + ')';
}

// The words a query was built from, for showing it and for relaxing it. Every
// word appears as a title: clause, so they can be read straight back out.
function plainWordsFrom(query) {
	const chunks = query.split('title:"');
	if (chunks.length < 2) return query.replace(/"/g, '').trim();
	const seen = [];
	chunks.slice(1).forEach((chunk) => {
		const word = chunk.split('"')[0];
		if (word && !seen.includes(word)) seen.push(word);
	});
	return seen.join(' ');
}

// A query that simply names a shelf of the catalogue — "chair", "stools",
// "wall art" — is a request for that shelf, not for everything whose copy
// mentions it. Shopify searches descriptions, and the styling notes on side
// tables and stools say "pairs beautifully with a chair", so a bare "chair"
// came back with more stools (375) than chairs (300). Narrowing to the named
// Product Types leaves 227 chairs, and the chip it raises says so and undoes
// it in one click.
//
// Only a whole-query match counts. "chair" names a shelf; "french chair" and
// "chair rail" describe something within or beside one, and narrowing those
// would hide what the shopper actually asked for.
function taxonomyTypesFor(raw, scopeMap) {
	const wanted = normaliseTaxonomyLabel(raw);
	if (!wanted || !scopeMap) return [];
	return scopeMap.get(wanted) || [];
}

// The taxonomy indexed by node name, every level of it: a top category and a
// tier-1 group both answer with the Product Types beneath them, and a Product
// Type answers with itself. Built from the map rendered onto the element, so
// the taxonomy is still declared in exactly one place.
function buildScopeIndex(text) {
	const index = new Map();
	if (!text) return index;

	const add = (label, types) => {
		const key = normaliseTaxonomyLabel(label || '');
		if (!key) return;
		// A top category spans several rows, so union rather than overwrite.
		const merged = index.get(key) || [];
		types.forEach((type) => {
			if (!merged.includes(type)) merged.push(type);
		});
		index.set(key, merged);
	};

	text.split(';').forEach((row) => {
		const parts = row.split('|');
		if (parts.length < 3) return;
		const types = [];
		parts[2]
			.split(',')
			.map((value) => value.trim())
			.filter(Boolean)
			.forEach((label) => {
				const type = TAXONOMY_TYPE_ALIASES[label] || label;
				types.push(type);
				// Where the taxonomy's label differs from the Product Type,
				// the label has to answer too: the shelf reads
				// "Miscellaneous", only the type says "Miscellaneous Decor".
				if (type !== label) add(label, [type]);
			});
		if (!types.length) return;
		add(parts[0], types); // top category
		add(parts[1], types); // tier-1 group
		types.forEach((type) => add(type, [type]));
	});
	return index;
}

// The taxonomy's labels are the Product Type values one-for-one except where
// it aliases one; see the note in snippets/taxonomy-map.liquid.
const TAXONOMY_TYPE_ALIASES = { Miscellaneous: 'Miscellaneous Decor' };

// Plural and punctuation are noise here: "Chairs" and "chair" name the same
// shelf, and "Wall & Art" should answer to "wall art".
function normaliseTaxonomyLabel(value) {
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.trim()
		.split(' ')
		.filter((word) => word && word !== 'and')
		.map((word) => word.replace(/s$/, ''))
		.join(' ');
}

// A quoted query that found nothing: drop the quotes and try again.
//
// Built here rather than in Liquid because search.terms arrives HTML-escaped,
// so Liquid's version of this URL mangles anything with an entity in it —
// o'brien would go out as o&#39;brien. location.search has the real value.
// The Liquid href stays as the no-JS fallback.
function relaxSearch() {
	const link = document.querySelector('.search-relax');
	if (!link) return;
	let params;
	try {
		params = new URLSearchParams(window.location.search);
	} catch (e) {
		return; // leave the Liquid href alone
	}
	const bare = plainWordsFrom(params.get('q') || '');
	if (!bare) return;
	const url = '/search?q=' + encodeURIComponent(bare) + '&type=product&relaxed=1';
	link.setAttribute('href', url);
	window.location.replace(url);
}

// Says why a search was broadened, when the quoted query found nothing and
// main-search sent us here. Presentational only — Liquid can't read query
// params, so the notice has to be added on this side.
function noteRelaxedSearch() {
	let params;
	try {
		params = new URLSearchParams(window.location.search);
	} catch (e) {
		return;
	}
	const summary = document.querySelector('.search-summary');
	if (!summary) return;
	// The flag is the memory and the note is the idempotence check, and they
	// have to be separate: filtering in place swaps .search-summary's
	// innerHTML, which destroys the note but leaves the attribute, and the
	// URL it pushes drops relaxed=1. Keyed on the attribute alone the note
	// could never come back; keyed on the param alone it would come back
	// only until the first filter. The results are still the relaxed
	// query's, so the explanation should outlive both.
	if (params.get('relaxed') === '1') summary.dataset.relaxed = '1';
	if (summary.dataset.relaxed !== '1') return;
	if (summary.querySelector('.search-summary__note')) return;
	const note = document.createElement('span');
	note.className = 'search-summary__note';
	note.textContent = ' — no exact matches, showing close matches';
	summary.appendChild(note);
}

// ---------------------------------------------------------------------------
// In-collection search
//
// Shopify's storefront search cannot be restricted to a collection: its query
// language has no collection field, and collection:, collection_title: and
// filter.p.collection all come back store-wide. These products carry no
// collection-identifying tag either — theirs are logistics ("Container 9",
// "Freight") — so there is nothing to scope on. Searching from inside a past
// collection matches that collection's own products here instead.
//
// The cards stay Liquid's. The collection is paged in through the existing
// load-more chain, so the cards on hand already respect the active filters,
// the sort and the in-stock-then-sold-out phases; this only decides which of
// them to show. That is affordable because these collections are small (77 to
// 115 products, three to five pages), and it is why the all-products
// collection is excluded — see the note in main-collection-product-grid.
//
// Matching is against title, product type and tags, deliberately not the
// description. Descriptions are what make the store-wide search for "chair"
// return side tables whose copy says "pairs beautifully with a chair"; inside
// a hundred products there is nothing to gain by repeating that.
const COLLECTION_INDEX = new Map();

// Which search owns the grid — see applyCollectionSearch.
let collectionSearchRun = 0;

// A query that has been made belongs to the chip, not to the box — the box is
// for the next one. Liquid renders it empty, but an in-place update leaves the
// element standing, so a search made or cleared without a page load would
// otherwise leave the old words sitting there. Emptied when the query changes,
// and only then: a filter change must not discard something half-typed.
let lastSeenQuery = null;

function syncSearchBox() {
	const input = document.querySelector('[data-search-input]');
	if (!input) return;
	const query = plainWordsFrom(activeSearchQuery());
	if (lastSeenQuery === null) {
		// First look at this page. The chip already says it.
		if (query) input.value = '';
	} else if (query !== lastSeenQuery) {
		input.value = '';
	}
	lastSeenQuery = query;
}

// A shelf with nothing in stock is still worth looking at. Results are paged
// in stock first and sold out second, so a shelf that is entirely sold out
// renders an empty first phase — and the empty state carries no "load more",
// so the second phase sits behind a link that never appears and the sold-out
// pieces cannot be reached at all. Sculptures had nineteen of them.
//
// When the first phase comes back with nothing, go straight to the second.
// Only ever from the in-stock phase, so a filter with nothing behind it in
// either phase settles on the empty state instead of bouncing.
function fallBackToSoldOut() {
	// Hidden cards still count: an in-collection search that matched nothing
	// has a grid, and its own note to show.
	if (document.querySelector('.collection-grid__item')) return;
	let params;
	try {
		params = new URLSearchParams(window.location.search);
	} catch (e) {
		return;
	}
	if (params.get('filter.v.availability') !== '1') return;
	params.set('filter.v.availability', '0');
	const href = window.location.pathname + '?' + params.toString();
	const sheet = document.querySelector('[data-sheet-key="filters"]');
	if (sheet && typeof sheet.navigate === 'function') sheet.navigate(href);
	else window.location.replace(href);
}

function collectionSearchScope() {
	const box = document.querySelector('search-box[data-search-scope]');
	if (!box) return null;
	return { url: box.dataset.searchScope, title: box.dataset.searchScopeTitle || '' };
}

function activeSearchQuery() {
	try {
		return (new URLSearchParams(window.location.search).get('q') || '').trim();
	} catch (e) {
		return '';
	}
}

// One request per collection per page view: products.json caps at 250 and
// these collections are well inside that.
function collectionIndex(scopeUrl) {
	if (!COLLECTION_INDEX.has(scopeUrl)) {
		COLLECTION_INDEX.set(
			scopeUrl,
			fetch(scopeUrl + '/products.json?limit=250')
				.then((res) => (res.ok ? res.json() : { products: [] }))
				.then((data) =>
					(data.products || []).map((product) => ({
						id: String(product.id),
						type: product.product_type || '',
						name: normaliseHaystack(
							[product.title, product.product_type, (product.tags || []).join(' ')].join(' ')
						),
						// Kept apart from the name, not mixed into it: descriptions
						// only get a say once nothing else has answered.
						body: normaliseHaystack((product.body_html || '').replace(/<[^>]*>/g, ' ')),
					}))
				)
				.catch(() => [])
		);
	}
	return COLLECTION_INDEX.get(scopeUrl);
}

function normaliseHaystack(value) {
	return value.toLowerCase().replace(/[^a-z0-9]+/g, ' ');
}

// Only the query is stemmed, never the text it is matched against. Stemming
// both sides breaks the common case in one direction — "tables" and "table"
// would reduce to "tabl" and "table", and "table" is not inside "tabl" — so
// the query is cut back and then looked for inside the untouched words.
// "chairs" finds Chair and Armchair; "table" still finds Tables.
function searchStems(value) {
	return value
		.toLowerCase()
		.replace(/[^a-z0-9]+/g, ' ')
		.trim()
		.split(' ')
		.filter(Boolean)
		.map((word) => (word.length > 3 ? word.replace(/(ies|es|s)$/, '') : word));
}

// One wrong letter, which is roughly where Shopify's spelling correction
// steps in. Short words are left alone — at three letters an edit is a
// different word.
function looselyIncludes(text, stem) {
	if (text.includes(stem)) return true;
	if (stem.length < 4) return false;
	return text.split(' ').some((word) => withinOneEdit(word, stem));
}

function withinOneEdit(a, b) {
	if (Math.abs(a.length - b.length) > 1) return false;
	// Two letters swapped is the typo people actually make — "chiar" for
	// "chair" — and it costs two substitutions, so the single-edit walk below
	// never forgives it. Check for it first.
	if (a.length === b.length) {
		const off = [];
		for (let k = 0; k < a.length; k += 1) {
			if (a[k] !== b[k]) off.push(k);
			if (off.length > 2) return false;
		}
		if (off.length === 2) {
			const [x, y] = off;
			return y === x + 1 && a[x] === b[y] && a[y] === b[x];
		}
	}
	let i = 0;
	let j = 0;
	let edits = 0;
	while (i < a.length && j < b.length) {
		if (a[i] === b[j]) {
			i += 1;
			j += 1;
			continue;
		}
		edits += 1;
		if (edits > 1) return false;
		if (a.length > b.length) i += 1;
		else if (a.length < b.length) j += 1;
		else {
			i += 1;
			j += 1;
		}
	}
	return edits + (a.length - i) + (b.length - j) <= 1;
}

// Widening rings, and the first one that answers wins — so a query that has a
// good answer never sees the looser readings, and only a query with no answer
// at all falls through to them.
//
//   1. every word in the title, product type or tags — or the query names a
//      shelf of the taxonomy, which takes the whole shelf
//   2. the descriptions get a say
//   3. one wrong letter per word
//
// Every word has to land, in whichever ring answers, so "french chair" stays
// narrower than either word alone.
function matchingProductIds(index, query, scopeIndex) {
	const stems = searchStems(query);
	if (!stems.length) return new Set();

	// "seating" finds benches and stools, which say "seating" nowhere — the
	// same reading the store-wide search gives it.
	const shelf = new Set(taxonomyTypesFor(query, scopeIndex));

	const pick = (test) => {
		const ids = new Set();
		index.forEach((entry) => {
			if (test(entry)) ids.add(entry.id);
		});
		return ids;
	};

	const byName = pick(
		(entry) => shelf.has(entry.type) || stems.every((stem) => entry.name.includes(stem))
	);
	if (byName.size) return byName;

	// Store-wide, descriptions are what bury a "chair" search in side tables
	// whose copy says "pairs beautifully with a chair". As a fallback across a
	// hundred products, they are the difference between a result and none.
	const byBody = pick((entry) =>
		stems.every((stem) => entry.name.includes(stem) || entry.body.includes(stem))
	);
	if (byBody.size) return byBody;

	return pick((entry) => stems.every((stem) => looselyIncludes(entry.name, stem)));
}

// Shown only when nothing matched. A count of what did is noise — the chip
// says what was searched and the grid says how it went — but an empty grid
// with no word on it reads as broken.
function collectionSearchNote(text) {
	let note = document.querySelector('.collection-search-note');
	if (!text) {
		note?.remove();
		return;
	}
	if (!note) {
		const wrap = document.querySelector('.collection__grid-wrap, .collection__empty');
		if (!wrap) return;
		note = document.createElement('p');
		note.className = 'collection-search-note';
		wrap.before(note);
	}
	note.textContent = text;
}

// The query reads as a chip beside the filter chips, the same as it does on
// the search template — it is one more thing narrowing the results, and it
// comes off the same way. Built here because Liquid cannot see the query
// string, so collection-active-chips has nothing to render it from; the
// markup matches chip--term there exactly — but it is found again by a marker
// of its own, never by that class: price and each dimension wear chip--term
// too, and removing "the term chip" by class ate whichever of them came
// first. On a collection, where this runs, a height filter lost its chip the
// moment it was applied and left only Clear all behind.
function paintCollectionSearchChip(query, clearHref) {
	const row = document.querySelector('.collection-chips');
	document.querySelector('[data-collection-search-chip]')?.remove();
	if (!row || !query) return;

	const chip = document.createElement('a');
	chip.className = 'chip chip--term';
	chip.dataset.collectionSearchChip = '';
	chip.href = clearHref;
	chip.dataset.sheetLink = '';
	chip.setAttribute('aria-label', 'Remove search term ' + query);

	const label = document.createElement('span');
	label.className = 'chip__label';
	label.textContent = '\u201c' + query + '\u201d';
	chip.appendChild(label);

	// Same path as collection-filter-icon's 'close'.
	const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
	svg.setAttribute('class', 'chip__x');
	svg.setAttribute('viewBox', '0 0 8 9');
	svg.setAttribute('aria-hidden', 'true');
	const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
	path.setAttribute(
		'd',
		'M7.586 1.711L4.773 4.523l2.79 2.79c.234.21.234.562 0 .773-.211.234-.563.234-.774 0L3.977 5.297l-2.79 2.789c-.21.234-.562.234-.773 0-.234-.211-.234-.563 0-.797l2.79-2.789-2.79-2.789c-.234-.21-.234-.562 0-.797.211-.21.563-.21.797 0L4 3.727 6.789.938c.21-.235.562-.235.797 0 .21.21.21.562 0 .773z'
	);
	path.setAttribute('fill', 'currentColor');
	svg.appendChild(path);
	chip.appendChild(svg);

	row.prepend(chip);
}

async function applyCollectionSearch() {
	const scope = collectionSearchScope();
	const host = document.querySelector('collection-load-more');
	if (!scope || !host) return;

	const query = activeSearchQuery();
	if (!query) {
		collectionSearchNote('');
		document.querySelector('[data-collection-search-chip]')?.remove();
		host.querySelectorAll('.collection-grid__item[hidden]').forEach((item) => {
			item.hidden = false;
		});
		return;
	}

	host.setAttribute('data-searching', '');

	// A search that replaces one already running must not have the older one
	// painting over it when it finishes. Whichever started last owns the grid.
	const run = (collectionSearchRun += 1);
	const current = () => run === collectionSearchRun;

	let ids = null;
	const paint = () => {
		if (!ids || !current()) return 0;
		let shown = 0;
		host.querySelectorAll('.collection-grid__item').forEach((item) => {
			const hit = ids.has(item.dataset.productId);
			item.hidden = !hit;
			if (hit) shown += 1;
		});
		return shown;
	};

	// The collection arrives a page at a time, and every page is a fetch. Held
	// back until the last one the grid sat dimmed for three and a half seconds
	// with the answer already in it — the first page usually holds matches.
	// Show them as soon as there is something to judge them against, and go on
	// sifting as the rest lands.
	const index = await collectionIndex(scope.url);
	if (!current()) return;
	ids = matchingProductIds(index, query, document.querySelector('search-box')?.scopeIndex);
	paint();
	host.removeAttribute('data-searching');

	// With the results. It says what is being searched, so it belongs on
	// screen the moment anything is filtered rather than after the last page.
	// Clearing drops the query and keeps everything else that is narrowed.
	const clearUrl = new URL(window.location.href);
	clearUrl.searchParams.delete('q');
	paintCollectionSearchChip(query, clearUrl.pathname + clearUrl.search);

	await host.loadEveryPage(paint);
	if (!current()) return;
	const shown = paint();

	// Only once every page is in can this be said at all — and only when there
	// is a grid to say it about. Where the filters alone left the server with
	// nothing to render, its own empty state has already said this, and a
	// second copy of the same sentence appeared directly above it.
	if (!host.querySelector('.collection-grid')) {
		collectionSearchNote('');
		return;
	}

	// Anything else in the chip row is holding the results down as much as the
	// query is, so the query cannot be named as the reason on its own.
	const alsoFiltered =
		document.querySelectorAll('.collection-chips .chip:not(.chip--clear):not([data-collection-search-chip])')
			.length > 0;
	collectionSearchNote(
		shown
			? ''
			: alsoFiltered
				? 'No products match all of the filters above. Try removing one.'
				: 'Nothing in ' + scope.title + ' matches \u201c' + query + '\u201d.'
	);
}

// Mobile shows only a search icon so Search / Filter / Sort fit on one line;
// expanding gives the field the full row and pushes Filter and Sort below.
// Above 768px the CSS keeps the field open and hides the toggle, so this
// element does nothing there.
class SearchBox extends HTMLElement {
	connectedCallback() {
		this.input = this.querySelector('[data-search-input]');
		const toggle = this.querySelector('[data-search-toggle]');
		if (!this.input) return;

		// The taxonomy does not change with the results, and applySection
		// leaves this element alone, so index it once.
		this.scopeIndex = buildScopeIndex(this.dataset.scopeMap);

		// Rewritten here rather than in the input itself, so the box keeps
		// showing what was typed instead of the quoted form.
		this.form = this.querySelector('form[role="search"]');
		if (this.form) {
			this.form.addEventListener('submit', (event) => {
				const raw = this.input.value.trim();
				if (!raw) return;
				const url = new URL(this.form.getAttribute('action'), window.location.origin);

				// Carry whatever is already narrowed. The form's action is bare
				// /search, so building from it alone dropped every filter: a
				// search made while looking at Chairs over $100 came back as
				// every oak thing in the shop. The param names are the same on
				// both templates, so they survive the hop, and the sort goes
				// with them — search keeps price order and quietly falls back
				// to relevance for the collection-only sorts.
				try {
					new URLSearchParams(window.location.search).forEach((value, key) => {
						if (key.startsWith('filter.') || key === 'sort_by') {
							url.searchParams.append(key, value);
						}
					});
				} catch (e) {
					// No filters carried; the search itself still works.
				}

				// Inside a past collection the search stays there and is
				// matched on the client, so none of the query rewriting below
				// applies — it exists to steer Shopify's engine, which is not
				// the one answering here.
				const scope = this.dataset.searchScope;
				if (scope) {
					const scoped = new URL(scope, window.location.origin);
					try {
						new URLSearchParams(window.location.search).forEach((value, key) => {
							if (key.startsWith('filter.') || key === 'sort_by') {
								scoped.searchParams.append(key, value);
							}
						});
					} catch (e) {
						// No filters carried; the search itself still works.
					}
					scoped.searchParams.set('q', raw);
					const scopedHref = scoped.pathname + scoped.search;
					event.preventDefault();
					const scopeSheet = document.querySelector('[data-sheet-key="filters"]');
					if (scopeSheet && typeof scopeSheet.navigate === 'function') {
						scopeSheet.navigate(scopedHref);
					} else {
						window.location.href = scopedHref;
					}
					return;
				}

				url.searchParams.set('q', buildSearchQuery(raw, this.scopeIndex));
				url.searchParams.set('type', 'product');
				const href = url.pathname + url.search;

				// Refining a search from the search page swaps the section in
				// place, like every other control in the bar, so the page
				// doesn't jump back to the top. Searching from a collection
				// page is a real navigation to a different template, so it
				// still loads normally.
				const sheet = document.querySelector('[data-sheet-key="filters"]');
				if (sheet && typeof sheet.navigate === 'function' && sheet.isSamePath(href)) {
					event.preventDefault();
					sheet.navigate(href);
					return;
				}
				event.preventDefault();
				window.location.href = href;
			});
		}

		// Above 768px the CSS keeps the field open and hides the toggle, so
		// there may be nothing to bind the expand behaviour to.
		if (!toggle) return;

		toggle.addEventListener('click', () => {
			this.toggleAttribute('data-expanded', true);
			toggle.setAttribute('aria-expanded', 'true');
			this.input.focus();
		});

		// Collapse again on blur only when nothing was typed, so an empty box
		// doesn't sit open over the controls.
		this.input.addEventListener('blur', () => {
			if (this.input.value.trim() !== '') return;
			this.removeAttribute('data-expanded');
			toggle.setAttribute('aria-expanded', 'false');
		});
	}
}

customElements.define('search-box', SearchBox);
noteRelaxedSearch();
relaxSearch();
applyCollectionSearch();
syncSearchBox();
fallBackToSoldOut();
// Both sheets share one implementation; their markup decides the screens.
// customElements.define rejects a constructor that is already registered,
// so the second tag needs its own (empty) subclass rather than a second
// reference to FilterDrawer.
customElements.define('filter-sort', FilterDrawer);
customElements.define('sort-menu', class extends FilterDrawer {});
